import { useEffect } from 'react';

/**
 * iOS Safari viewport height fix
 * 
 * On iOS Safari, 100vh includes the browser chrome which causes content
 * to be hidden behind it. This hook sets a CSS variable --vh that represents
 * the actual visible viewport height.
 * 
 * Usage in CSS: height: calc(var(--vh, 1vh) * 100);
 */
export function useViewportHeight() {
  useEffect(() => {
    const setVH = () => {
      // Get the actual viewport height
      const vh = window.innerHeight * 0.01;
      // Set the CSS variable
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    // Set on mount
    setVH();

    // Update on resize and orientation change
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);

    // Cleanup
    return () => {
      window.removeEventListener('resize', setVH);
      window.removeEventListener('orientationchange', setVH);
    };
  }, []);
}

/**
 * Hook to lock body scroll when player is active
 * Prevents iOS Safari bounce/overscroll
 */
export function useLockBodyScroll(lock = true) {
  useEffect(() => {
    if (!lock) return;

    // Save original styles
    const originalStyle = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      width: document.body.style.width,
      height: document.body.style.height,
      top: document.body.style.top,
    };

    // Get current scroll position
    const scrollY = window.scrollY;

    // Lock body
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    document.body.style.top = `-${scrollY}px`;

    // Also lock html element for iOS
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.height = '100%';

    return () => {
      // Restore original styles
      document.body.style.overflow = originalStyle.overflow;
      document.body.style.position = originalStyle.position;
      document.body.style.width = originalStyle.width;
      document.body.style.height = originalStyle.height;
      document.body.style.top = originalStyle.top;
      document.documentElement.style.overflow = '';
      document.documentElement.style.height = '';

      // Restore scroll position
      window.scrollTo(0, scrollY);
    };
  }, [lock]);
}

export default useViewportHeight;
